// The solar estimate widget (docs/12-solar-roof-scan.md §1, §7).
//
// A homeowner enters an address, confirms their house on a satellite map, watches a
// roof "scan", sees panels laid on the roof best-first, picks how many with a slider
// and gets the installer's bundle price range, with an optional 3D view. Then they
// can ask for a callback.
//
// Same contract as the other widgets (render.ts): framework-free, rendered into a
// shadow root, themed by the installer's branding through --xq-* variables, and the
// SAME code runs on websites (real API) and in the console preview (mock API).
//
// The one big difference: the map. The other widgets re-render their whole canvas
// with innerHTML; here ONLY the step card (.xqs-card) is re-rendered. The map divs
// are created once and changed only through overlays.ts, because re-rendering the
// map container destroys the Google map and every overlay (§7.1).
//
// Thin client (§6): the roof engine and the price model run ONLY on the API, so none
// of the method ships in this public bundle. The API sends the roof ready to draw
// (outline, terrace slice, panel corners, summary) and, for the panels kept on the
// homeowner's roof, a table of figures per panel count (energy, savings, price range).
// The installer's price list never reaches the browser. All this file does is draw,
// animate, and move the trim handles (plain rectangle maths in handles.ts).
//
// Cost rules (§4.4): no map until the address step is done; ONE Building Insights
// request per pin (the scan reuses the confirm step's promise); 3D only on demand.
//
// Test hooks (§11): data-* attributes on .xqs-stage (step, outline, adjust, trim,
// panels-offered, panels-shown). No global debug hooks.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ensureGoogleFont } from "../fonts";
import { METHOD_STATEMENT_SOLAR } from "../method";
import { WIDGET_CSS, cssVars } from "../styles";
import type {
  Address,
  LL,
  Outline,
  SolarEstimate,
  SolarEstimateRow,
  SolarLead,
  SolarLocateRequest,
  SolarLocation,
  SolarRoof,
  SolarRoofPanel,
  SolarStep,
  Trim,
  WidgetApi,
  WidgetBranding,
} from "../types";
import { DEFAULT_BRANDING, SOLAR_COPY, SOLAR_DEFAULT_HEIGHT, SOLAR_LAUNCHER_LABEL } from "../types";
import { offset } from "./geo";
import { clampTrim, inSlice, outlineCorners, rowGeometry, trimCorners, trimWidthM, wholeRow, type RowGeometry } from "./handles";
import { loadMaps, wait } from "./maps";
import { createOverlays, MIN_SCAN_MS, type Overlays } from "./overlays";
import { SOLAR_CSS } from "./solarStyles";
import { createView3d, type View3d } from "./view3d";

export interface SolarMountOptions {
  branding: WidgetBranding;
  api: WidgetApi;
  /** Browser key for the Maps JavaScript API (from the session). */
  mapsKey?: string;
  contained?: boolean;
  highlight?: boolean;
  startOpen?: boolean;
  onStep?: (step: SolarStep) => void;
}

export interface SolarHandle {
  update(branding: WidgetBranding): void;
  goTo(step: SolarStep | string): Promise<void>;
  destroy(): void;
  /** Console: the draft prices changed, so ask for the result's figures again. */
  refresh?(): void;
}

// ─── Small helpers ───────────────────────────────────────────────────────────

const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UK_VIEW: LL = { lat: 54.2, lng: -2.4 }; // the starting view (zoom 6)

function esc(s: string | number): string {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
const gbp = (n: number) => `£${Math.round(n).toLocaleString("en-GB")}`;
const kwh = (n: number) => `${Math.round(n).toLocaleString("en-GB")}`;
const pinKey = (p: LL) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;

// The yearly electricity use the homeowner can enter (the API checks the same bounds).
// 2,700 kWh is Ofgem's typical domestic figure.
const USAGE = { DEFAULT: 2700, MIN: 500, MAX: 20000, STEP: 100 };

const ICON_CHECK =
  '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5 10 17l9-10"/></svg>';
const ICON_SUN =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>';
const XENSCOPE_GLYPH =
  '<svg viewBox="0 0 10 18" aria-hidden="true"><path d="M0 0 6.2 3.6Q10 5.8 10 9.2V9.6H0Z" fill="#E1EE2B"/><rect y="11" width="10" height="7" rx="2.4" fill="#014852"/></svg>';
// The address step's decorative roof (the map isn't loaded yet)
const START_ART =
  '<svg viewBox="0 0 200 140" aria-hidden="true"><path d="M20 70 100 20l80 50v60H20z" fill="rgba(255,255,255,.12)"/><g fill="rgba(255,255,255,.55)"><rect x="58" y="52" width="22" height="14" rx="2" transform="skewX(-12)"/><rect x="86" y="52" width="22" height="14" rx="2" transform="skewX(-12)"/><rect x="114" y="52" width="22" height="14" rx="2" transform="skewX(-12)"/><rect x="52" y="70" width="22" height="14" rx="2" transform="skewX(-12)"/><rect x="80" y="70" width="22" height="14" rx="2" transform="skewX(-12)"/><rect x="108" y="70" width="22" height="14" rx="2" transform="skewX(-12)"/></g></svg>';

const CHECKLIST = ["Finding your roof", "Measuring roof area and pitch", "Checking sunlight and shading", "Fitting panels to your roof"];
const CHIP = ["Finding your roof…", "Measuring roof area and pitch…", "Checking sunlight and shading…", "Fitting panels to your roof…"];

/** Installer wording, falling back to the solar defaults where the field is empty or
 *  still holds the heat pump default (normaliseBranding fills missing copy from it). */
function copyOf(b: WidgetBranding, key: keyof WidgetBranding["copy"]): string {
  const v = b.copy[key];
  return !v || v === DEFAULT_BRANDING.copy[key] ? SOLAR_COPY[key] : v;
}
function launcherLabel(b: WidgetBranding): string {
  const v = b.layout.launcherLabel;
  return !v || v === DEFAULT_BRANDING.layout.launcherLabel ? SOLAR_LAUNCHER_LABEL : v;
}

// ─── The widget ──────────────────────────────────────────────────────────────

interface State {
  step: SolarStep;
  open: boolean; // floating mode: the sheet is showing
  busy: boolean;
  error: string | null;
  // Address step
  manual: boolean;
  postcode: string;
  house: string;
  street: string; // manual entry: optional (some rural addresses have none)
  town: string;
  addresses: Address[];
  chosen: { line?: string; uprn?: string; house?: string; postcode: string } | null;
  // Confirm step
  location: SolarLocation | null;
  pin: LL | null;
  approx: boolean;
  justMoved: boolean;
  acceptMapClicks: boolean;
  buildingReady: boolean;
  building: SolarRoof | null; // the roof as the API drew it for this pin
  buildingError: { message: string; code?: string } | null;
  outline: Outline | null;
  fitted: boolean; // the API fitted the outline to the panels (so it can be rotated)
  geo: RowGeometry | null;
  adjust: "off" | "manual" | "auto";
  trim: Trim | null;
  // Scan / result
  checklist: number; // the active checklist item (4 = all done)
  kept: SolarRoofPanel[]; // the panels on the homeowner's slice of the roof, best first
  estimate: SolarEstimate | null; // the API's figures for the kept panels
  offered: SolarRoofPanel[]; // the panels the slider can show (kept, up to estimate.max)
  minP: number;
  maxP: number;
  n: number;
  recommended: number;
  usage: number;
  battery: boolean;
  show3d: boolean;
  noscanReason: string;
  fromNoscan: boolean;
  // Contact
  name: string;
  email: string;
  phone: string;
  consent: boolean;
}

export function mountSolarWidget(host: HTMLElement, opts: SolarMountOptions): SolarHandle {
  let branding = opts.branding;
  const api = opts.api;

  // ── Shadow root and the persistent skeleton (§7.1) ───────────────────────
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
  shadow.innerHTML = "";
  const style = document.createElement("style");
  style.textContent = WIDGET_CSS + SOLAR_CSS;
  const root = document.createElement("div");
  shadow.append(style, root);

  // The widget body is built ONCE: the stage (map) must never be re-rendered
  const body = document.createElement("div");
  body.className = "xqs";
  body.innerHTML = `<div class="xqs-frame">
      <div class="xqs-stage" data-step="address" data-adjust="off" data-panels-offered="0" data-panels-shown="0">
        <div class="xqs-start" data-xqs-start>${START_ART}</div>
        <div class="xqs-mapwrap"><div class="xqs-map"></div></div>
        <div class="xqs-map3d" hidden></div>
        <div class="xqs-chip" hidden><span class="xqs-chip-dot"></span><span data-xqs-chip></span></div>
        <section class="xqs-card" data-xqs-card aria-live="polite"></section>
      </div>
      <div class="xq-foot">Powered by <a href="https://xenscope.com" target="_blank" rel="noopener">${XENSCOPE_GLYPH}xenscope</a></div>
    </div>`;
  const stage = body.querySelector<HTMLElement>(".xqs-stage")!;
  const startPanel = body.querySelector<HTMLElement>("[data-xqs-start]")!;
  const mapDiv = body.querySelector<HTMLElement>(".xqs-map")!;
  const mapWrap = body.querySelector<HTMLElement>(".xqs-mapwrap")!;
  const map3dDiv = body.querySelector<HTMLElement>(".xqs-map3d")!;
  const chip = body.querySelector<HTMLElement>(".xqs-chip")!;
  const chipText = body.querySelector<HTMLElement>("[data-xqs-chip]")!;
  const card = body.querySelector<HTMLElement>("[data-xqs-card]")!;

  // Floating: a launcher, and a full-screen sheet holding the same body
  const launcher = document.createElement("div");
  const sheet = document.createElement("div");
  sheet.className = "xqs-sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  const sheetClose = document.createElement("button");
  sheetClose.type = "button";
  sheetClose.className = "xqs-sheet-close";
  sheetClose.setAttribute("aria-label", "Close");
  sheetClose.textContent = "×";

  const state: State = {
    step: "address",
    open: opts.startOpen ?? false,
    busy: false,
    error: null,
    manual: false,
    postcode: "",
    house: "",
    street: "",
    town: "",
    addresses: [],
    chosen: null,
    location: null,
    pin: null,
    approx: false,
    justMoved: false,
    acceptMapClicks: false,
    buildingReady: false,
    building: null,
    buildingError: null,
    outline: null,
    fitted: false,
    geo: null,
    adjust: "off",
    trim: null,
    checklist: 0,
    kept: [],
    estimate: null,
    offered: [],
    minP: 0,
    maxP: 0,
    n: 0,
    recommended: 0,
    usage: USAGE.DEFAULT,
    battery: false,
    show3d: false,
    noscanReason: "",
    fromNoscan: false,
    name: "",
    email: "",
    phone: "",
    consent: false,
  };

  // Map objects (created on first use, never re-rendered)
  let g: any = null;
  let map: any = null;
  let overlays: Overlays | null = null;
  let mapReady: Promise<void> | null = null;
  let flownIn = false;
  let pending: { promise: Promise<SolarRoof>; forPin: string } | null = null;
  let estimating = 0; // the latest estimate request (an older answer is dropped)
  let view3d: View3d | null = null;
  let opened = false; // funnel "opened" sent
  let destroyed = false;

  // ─── Layout (inline or floating) and theme ────────────────────────────────

  function applyTheme() {
    ensureGoogleFont(branding.font.family);
    const vars = cssVars(branding);
    for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
    // The map's height: the installer's "Map height", or the solar default
    root.style.setProperty("--xqs-height", `${branding.layout.height || SOLAR_DEFAULT_HEIGHT}px`);
    root.className = `xq-root${opts.contained ? " xq-contained" : ""}${opts.highlight ? " xq-highlight" : ""}`;
    overlays?.setColors({ accent: branding.colors.accent, accentText: vars["--xq-accent-text"] });
  }

  function layout() {
    applyTheme();
    if (branding.layout.mode === "floating") {
      launcher.className = `xq-floating xq-pos-${branding.layout.position}`;
      launcher.innerHTML = `<button type="button" class="xq-launcher" data-action="open">${ICON_SUN}${esc(launcherLabel(branding))}</button>`;
      if (!sheet.contains(body)) sheet.append(sheetClose, body);
      root.replaceChildren(state.open ? sheet : launcher);
    } else {
      root.replaceChildren(body);
    }
  }

  launcher.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest("[data-action=open]")) {
      state.open = true;
      layout();
      markOpened();
    }
  });
  sheetClose.addEventListener("click", () => {
    state.open = false;
    layout();
  });

  // ─── Card rendering (the only innerHTML re-render) ────────────────────────

  function setStep(step: SolarStep) {
    state.step = step;
    stage.dataset.step = step;
    if (step === "contact") api.track?.("contact");
    opts.onStep?.(step);
  }

  function setChip(text: string | null) {
    chip.hidden = !text;
    chipText.textContent = text ?? "";
  }

  /** Keep the test attributes (§11) in step with the state. */
  function syncData() {
    const o = state.outline;
    stage.dataset.adjust = state.adjust;
    if (o) stage.dataset.outline = `${o.len.toFixed(1)}x${o.wid.toFixed(1)}@${(((o.heading % 180) + 180) % 180).toFixed(1)}`;
    else delete stage.dataset.outline;
    if (state.trim && state.adjust !== "off") stage.dataset.trim = `${state.trim.t0.toFixed(2)}-${state.trim.t1.toFixed(2)}`;
    else delete stage.dataset.trim;
    stage.dataset.panelsOffered = String(state.offered.length);
    stage.dataset.panelsShown = String(state.step === "result" ? state.n : 0);
  }

  function logoHtml(): string {
    return branding.logoUrl && /^https:\/\//.test(branding.logoUrl) ? `<img class="xq-logo" src="${esc(branding.logoUrl)}" alt="" style="margin-bottom:14px">` : "";
  }

  function render() {
    if (destroyed) return;
    const err = state.error ? `<p class="xq-error" role="alert">${esc(state.error)}</p>` : "";
    card.innerHTML = cardHtml() + err;
    syncData();
    if (state.step === "result") updateResultFigures();
  }

  function cardHtml(): string {
    switch (state.step) {
      case "address":
        return addressHtml();
      case "confirm":
        return confirmHtml();
      case "scan":
        return scanHtml();
      case "result":
        return resultHtml();
      case "noscan":
        return noscanHtml();
      case "contact":
        return contactHtml();
      case "done":
        return `<div class="xq-done"><div class="xq-done-icon">${ICON_CHECK}</div>
          <h2 class="xq-title">${esc(copyOf(branding, "thankYouTitle"))}</h2>
          <p class="xq-sub">${esc(copyOf(branding, "thankYouText"))}</p></div>`;
    }
  }

  // Step 1: address (no map yet)
  function addressHtml(): string {
    const head = `${logoHtml()}<h2 class="xq-title">${esc(copyOf(branding, "title"))}</h2><p class="xq-sub">${esc(copyOf(branding, "subtitle"))}</p>`;
    if (state.manual) {
      return `${head}
        <form data-form="manual" class="xqs-manual" novalidate>
          <div class="xq-field"><label class="xq-label" for="xqs-house">House name or number</label>
            <input id="xqs-house" class="xq-input" name="house" autocomplete="address-line1" value="${esc(state.house)}"></div>
          <div class="xq-field"><label class="xq-label" for="xqs-street">Street (if there is one)</label>
            <input id="xqs-street" class="xq-input" name="street" autocomplete="address-line2" value="${esc(state.street)}"></div>
          <div class="xq-field"><label class="xq-label" for="xqs-town">Town or village</label>
            <input id="xqs-town" class="xq-input" name="town" autocomplete="address-level2" value="${esc(state.town)}"></div>
          <div class="xq-field"><label class="xq-label" for="xqs-mpc">Postcode</label>
            <input id="xqs-mpc" class="xq-input" name="postcode" autocomplete="postal-code" value="${esc(state.postcode)}" placeholder="e.g. HD7 5UU"></div>
          <button class="xq-btn xq-btn-block" ${state.busy ? "disabled" : ""}>${esc(copyOf(branding, "buttonText"))}</button>
        </form>
        <button type="button" class="xqs-link" data-action="list">Back to the address list</button>`;
    }
    const list = state.addresses.length
      ? `<div class="xq-list" style="margin-top:14px">${state.addresses
          .map((a) => `<button type="button" class="xq-option" data-addr="${esc(a.id)}">${esc(a.line)}</button>`)
          .join("")}</div>`
      : "";
    return `${head}
      <form data-form="postcode" novalidate>
        <label class="xq-label" for="xqs-pc">Postcode</label>
        <div class="xq-row">
          <input id="xqs-pc" class="xq-input" name="postcode" autocomplete="postal-code" value="${esc(state.postcode)}" placeholder="e.g. HD7 5UU">
          <button class="xq-btn" ${state.busy ? "disabled" : ""}>Find address</button>
        </div>
      </form>
      ${list}
      <button type="button" class="xqs-link" data-action="manual">My address isn't listed</button>`;
  }

  // Step 2: confirm (§7.3)
  function confirmHtml(): string {
    const rotateSentence = state.fitted ? " Drag the round handle to rotate the outline." : "";
    let hint = "";
    if (state.adjust === "auto")
      hint = `This looks like a terrace. Drag the white handles to the walls of your house, or tap your house in the row.${rotateSentence}`;
    else if (state.adjust === "manual") hint = `Drag the white handles to the walls of your house if you share the roof.${rotateSentence}`;
    else if (state.justMoved) hint = "Moved. Check the outline is on the right roof.";
    else if (state.approx) hint = "We could only find your postcode, not the exact house. Drag the outline onto your house, or tap your roof.";
    let readout = "";
    if (state.adjust !== "off" && state.geo && state.trim && state.building) {
      const fit = inSlice(state.building.panels, state.trim, state.geo).length;
      readout = `<p class="xqs-readout">Your house: ${trimWidthM(state.geo, state.trim).toFixed(1)} m wide · ${fit} panel${fit === 1 ? "" : "s"} fit</p>`;
    }
    const adjustLink =
      state.adjust === "off" && state.outline && state.buildingReady
        ? `<button type="button" class="xqs-link" data-action="adjust" style="margin:0 0 12px">Not quite right, or share a roof? Adjust the outline</button>`
        : "";
    return `<p class="xqs-eyebrow">${esc(state.location?.address ?? "")}</p>
      <h2 class="xq-title">Is this your home?</h2>
      ${hint ? `<p class="xqs-hint">${esc(hint)}</p>` : ""}
      <p class="xqs-note">We've outlined the roof we'll scan. Wrong house? Drag the outline onto yours, or tap your roof.</p>
      ${readout}${adjustLink}
      <div class="xqs-actions">
        <button type="button" class="xq-btn xq-btn-block" data-action="yes" ${state.buildingReady ? "" : "disabled"}>${state.buildingReady ? "Yes, scan my roof" : "Finding your roof…"}</button>
        <button type="button" class="xq-btn xq-btn-ghost xq-btn-block" data-action="move">No, let me move it to my house</button>
      </div>`;
  }

  // Step 3: scan (§5.7)
  function scanHtml(): string {
    return `<p class="xqs-eyebrow">${esc(state.location?.address ?? "")}</p>
      <h2 class="xq-title">Scanning your roof</h2>
      <ul class="xqs-checklist">${CHECKLIST.map(
        (t, i) => `<li class="${i < state.checklist ? "is-done" : i === state.checklist ? "is-active" : ""}">${esc(t)}</li>`,
      ).join("")}</ul>`;
  }

  // Step 4: result (§7.5). Figures are filled by updateResultFigures (no re-render on slider moves)
  function resultHtml(): string {
    const est = state.estimate!;
    const has3d = Boolean(state.building?.view3d);
    const showBattery = est.battery.offered;
    const title = copyOf(branding, "quoteTitle").replace("{n}", String(state.kept.length));
    return `<p class="xqs-eyebrow">${esc(state.location?.address ?? "")}</p>
      <h2 class="xq-title">${esc(title)}</h2>
      <p class="xqs-summary">${esc(state.building?.summary ?? "")}</p>
      <div class="xqs-slider">
        <div class="xqs-slider-top"><span><b data-r="n"></b> panels · <span data-r="kwp"></span> kWp</span><span class="xqs-badge" data-r="badge">Recommended</span></div>
        <input type="range" data-action="slider" min="${state.minP}" max="${state.maxP}" step="1" value="${state.n}" aria-label="Number of panels">
        <div class="xqs-slider-ends"><span>${state.minP}</span><span>${state.maxP}</span></div>
      </div>
      <div class="xqs-stats">
        <div class="xqs-stat"><b data-r="kwh"></b><span>kWh per year</span></div>
        <div class="xqs-stat"><b data-r="saving"></b><span>est. saving per year</span></div>
        <div class="xqs-stat"><b data-r="cover"></b><span>of your usage</span></div>
      </div>
      <details class="xqs-usage">
        <summary>Based on <span data-r="usage"></span> kWh/year usage. <b>Change</b></summary>
        <form data-form="usage" class="xq-row" novalidate>
          <input class="xq-input" name="usage" type="number" min="${USAGE.MIN}" max="${USAGE.MAX}" step="${USAGE.STEP}" value="${state.usage}" aria-label="Yearly electricity use in kWh">
          <button class="xq-btn" ${state.busy ? "disabled" : ""}>Update</button>
        </form>
      </details>
      ${showBattery ? `<label class="xqs-toggle"><input type="checkbox" data-action="battery" ${state.battery ? "checked" : ""}> Add a ${esc(est.battery.label)}</label>` : ""}
      <div class="xqs-price">
        <div class="xqs-price-label">Estimated price</div>
        <div class="xq-quote-range" data-r="range"></div>
        <div class="xqs-price-kit" data-r="kit"></div>
        ${est.packagesNote ? `<div class="xqs-price-note">${esc(est.packagesNote)}</div>` : ""}
        <div class="xqs-price-note">${esc(copyOf(branding, "disclaimer"))}</div>
      </div>
      <details class="xq-method"><summary>How is this estimate worked out?</summary><p>${esc(METHOD_STATEMENT_SOLAR)}</p></details>
      <div class="xqs-actions" style="margin-top:14px">
        ${has3d ? `<button type="button" class="xq-btn xq-btn-ghost xq-btn-block" data-action="3d" style="margin:0 0 10px">${state.show3d ? "Back to 2D view" : "View in 3D"}</button>` : ""}
        <button type="button" class="xq-btn xq-btn-block" data-action="quote">Get my quote</button>
      </div>
      <button type="button" class="xqs-link" data-action="notmine">Not your house? Tap your roof on the map</button>`;
  }

  /** Update the result figures in place (slider moves must not replace the slider). */
  /** The API's row for n panels (every count from min to max has one). */
  function rowFor(n: number): SolarEstimateRow | null {
    return state.estimate?.rows.find((r) => r.n === n) ?? null;
  }

  /** The battery is in the price only if the homeowner ticked it and the installer offers one. */
  const withBattery = () => state.battery && Boolean(state.estimate?.battery.offered);

  /** "£6,450 – £7,900" for a row, with or without the battery. */
  function rangeText(r: SolarEstimateRow): string {
    const [lo, hi] = withBattery() ? [r.lowBattery, r.highBattery] : [r.low, r.high];
    return lo == null || hi == null ? "Price on survey" : `${gbp(lo)} – ${gbp(hi)}`;
  }

  /** Update the result figures in place from the API's table (no maths here). */
  function updateResultFigures() {
    const est = state.estimate;
    const r = rowFor(state.n);
    if (state.step !== "result" || !est || !r) return;
    const set = (k: string, v: string) => {
      const el = card.querySelector<HTMLElement>(`[data-r="${k}"]`);
      if (el) el.textContent = v;
    };
    set("n", String(r.n));
    set("kwp", r.kwp.toFixed(2));
    const badge = card.querySelector<HTMLElement>('[data-r="badge"]');
    if (badge) badge.hidden = state.n !== state.recommended;
    set("kwh", kwh(r.kwh));
    set("saving", gbp(withBattery() ? r.savingBattery : r.saving));
    set("cover", `${r.coverage}%`);
    set("usage", kwh(state.usage));
    set("range", rangeText(r));
    set("kit", `${r.n} × ${est.panelModel}${r.inverter ? ` + ${r.inverter}` : ""}${withBattery() ? ` + ${est.battery.label}` : ""}`);
    stage.dataset.panelsShown = String(state.n);
  }

  // Step 4b: noscan
  function noscanHtml(): string {
    return `<p class="xqs-eyebrow">${esc(state.location?.address ?? "")}</p>
      <h2 class="xq-title">We couldn't scan this roof automatically</h2>
      <p class="xq-sub">${esc(state.noscanReason)}</p>
      <div class="xqs-actions">
        <button type="button" class="xq-btn xq-btn-block" data-action="ask">Ask for a quote</button>
        <button type="button" class="xq-btn xq-btn-ghost xq-btn-block" data-action="restart">Try another address</button>
      </div>`;
  }

  // Step 5: contact
  function contactHtml(): string {
    let summary = "We'll take a look at your roof and get back to you with a quote.";
    const r = rowFor(state.n);
    if (!state.fromNoscan && r && state.estimate) {
      summary = `${r.n} panels · ${r.kwp.toFixed(2)} kWp${withBattery() ? ` · ${state.estimate.battery.label}` : ""} · ${rangeText(r)}`;
    }
    return `<p class="xqs-eyebrow">${esc(state.location?.address ?? "")}</p>
      <h2 class="xq-title">Get your quote</h2>
      <p class="xq-sub">${esc(summary)}</p>
      <form data-form="contact" novalidate>
        <div class="xq-field"><label class="xq-label" for="xqs-name">Name</label><input id="xqs-name" class="xq-input" name="name" autocomplete="name" value="${esc(state.name)}"></div>
        <div class="xq-field"><label class="xq-label" for="xqs-email">Email</label><input id="xqs-email" class="xq-input" name="email" type="email" autocomplete="email" value="${esc(state.email)}"></div>
        <div class="xq-field"><label class="xq-label" for="xqs-phone">Phone (optional)</label><input id="xqs-phone" class="xq-input" name="phone" type="tel" autocomplete="tel" value="${esc(state.phone)}"></div>
        <label class="xq-consent"><input type="checkbox" name="consent" ${state.consent ? "checked" : ""}> <span>${esc(copyOf(branding, "consentText"))}</span></label>
        <button class="xq-btn xq-btn-block" ${state.busy ? "disabled" : ""}>Send</button>
      </form>
      <button type="button" class="xqs-link" data-action="back">Back</button>`;
  }

  // ─── The map ──────────────────────────────────────────────────────────────

  /** Load Google Maps and build the 2D map once (§7.2). */
  function ensureMap(): Promise<void> {
    if (mapReady) return mapReady;
    mapReady = (async () => {
      g = { maps: await loadMaps(opts.mapsKey) };
      if (destroyed) return;
      map = new g.maps.Map(mapDiv, {
        center: UK_VIEW,
        zoom: 6,
        mapTypeId: "satellite",
        tilt: 0,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "greedy",
        clickableIcons: false,
      });
      const vars = cssVars(branding);
      overlays = createOverlays(g, map, mapDiv, { accent: branding.colors.accent, accentText: vars["--xq-accent-text"] }, {
        onOutlineMoved: (p) => {
          state.approx = false;
          void enterConfirm(p, { moved: true });
        },
        onTrimHandle: (h, t) => {
          if (!state.trim || !state.geo) return;
          state.trim = clampTrim(state.trim, h, t, state.geo.L);
          overlays!.drawTrim(state.geo, state.trim);
          render();
        },
        onRotate: (heading) => {
          if (!state.outline) return;
          state.outline = { ...state.outline, heading };
          applyOutline();
        },
      });
      map.addListener("click", (e: any) => {
        if (!state.acceptMapClicks || state.step !== "confirm" || !e.latLng) return;
        const at = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        // In adjust mode a tap re-centres the slice along the row (no new request)
        if (state.adjust !== "off" && state.geo && state.trim) {
          const span = state.trim.t1 - state.trim.t0;
          const t0 = Math.max(0, Math.min(1 - span, state.geo.tOf(at) - span / 2));
          state.trim = { t0, t1: t0 + span };
          overlays!.drawTrim(state.geo, state.trim);
          render();
          return;
        }
        state.approx = false;
        void enterConfirm(at, { moved: true });
      });
      startPanel.hidden = true;
    })();
    mapReady.catch(() => {
      mapReady = null;
    });
    return mapReady;
  }

  /** Centre a point in the part of the map the card doesn't cover. Under 720 px the
   *  card is a bottom sheet, so the point is moved up by half the sheet's height. */
  function centreOn(at: LL) {
    if (!map) return;
    map.panTo(at);
    if (stage.clientWidth < 720 && card.offsetHeight) map.panBy(0, card.offsetHeight / 2);
  }

  /** Fly in to a point (§7.2): zoom 12 first, pan, then one zoom level at a time to 20. */
  async function flyTo(at: LL, zoom = 20) {
    if (!map) return;
    if (map.getZoom() < 12) {
      map.setZoom(12);
      await wait(250);
    }
    centreOn(at);
    await wait(400);
    for (let z = Math.max(13, map.getZoom() + 1); z <= zoom; z++) {
      if (destroyed) return;
      map.setZoom(z);
      centreOn(at);
      await wait(320);
    }
  }

  /** Redraw the outline (and trim) after a rotate: rebuild the row, keep t0/t1. */
  function applyOutline() {
    if (!state.outline || !overlays) return;
    overlays.updateOutline(state.outline);
    state.geo = rowGeometry(outlineCorners(state.outline));
    if (state.trim) overlays.drawTrim(state.geo, state.trim);
    render();
  }

  /** Draw the outline (and trim in adjust mode) for the current state. */
  function drawConfirmOverlays() {
    if (!overlays || !state.outline) return;
    const corners = outlineCorners(state.outline);
    overlays.drawOutline(corners, state.fitted ? state.outline : null, state.adjust !== "off");
    if (state.adjust !== "off" && state.geo && state.trim) overlays.drawTrim(state.geo, state.trim);
    else overlays.clearTrim();
  }

  /** Show the roof the API drew (§7.3 step 2): its outline, and on a terrace its starting slice. */
  function useBuilding(b: SolarRoof) {
    state.building = b;
    state.fitted = b.fitted;
    state.outline = b.outline;
    state.geo = rowGeometry(outlineCorners(state.outline));
    if (b.terrace && b.trim) {
      // A terrace: straight into adjust mode with the API's house-width slice on the pin
      state.adjust = "auto";
      state.trim = b.trim;
      state.pin = state.geo.at((state.trim.t0 + state.trim.t1) / 2, 0.5);
    } else {
      state.adjust = "off";
      state.trim = null;
      state.pin = b.center;
    }
    overlays?.pin.place(state.pin, false);
    centreOn(state.pin);
    drawConfirmOverlays();
  }

  /**
   * The confirm step for a pin (§7.3): clear the map, drop the pin, start ONE Building
   * Insights request for it (the scan reuses this promise), and fly in on first arrival.
   */
  async function enterConfirm(at: LL, o: { moved?: boolean } = {}) {
    state.error = null;
    try {
      await ensureMap();
    } catch (e) {
      state.error = (e as Error).message;
      render();
      return;
    }
    if (destroyed) return;
    view3dOff();
    overlays!.clearAll();
    setChip(null);
    setStep("confirm");
    state.pin = at;
    state.justMoved = Boolean(o.moved);
    state.acceptMapClicks = true;
    state.buildingReady = false;
    state.building = null;
    state.buildingError = null;
    state.outline = null;
    state.fitted = false;
    state.geo = null;
    state.adjust = "off";
    state.trim = null;
    state.offered = [];
    overlays!.pin.place(at, true);
    render();

    const req = { promise: api.solarBuilding!(at), forPin: pinKey(at) };
    pending = req;
    if (!flownIn) {
      flownIn = true;
      void flyTo(at);
    } else centreOn(at);

    try {
      const b = await req.promise;
      if (pending !== req || destroyed) return; // a newer pin superseded this one
      useBuilding(b);
    } catch (e) {
      if (pending !== req) return;
      const err = e as Error & { code?: string };
      state.buildingError = { message: err.message, code: err.code };
    } finally {
      if (pending === req) {
        // "Yes" is enabled once the request has settled, success or failure
        state.buildingReady = true;
        render();
      }
    }
  }

  /** Back to the confirm step for the building we already have (no new request). */
  function reconfirm() {
    if (!state.building || !state.pin) return;
    view3dOff();
    overlays?.clearAll();
    setStep("confirm");
    state.acceptMapClicks = true;
    state.justMoved = false;
    useBuilding(state.building);
    state.buildingReady = true;
    setChip("Tap your roof on the map");
    render();
  }

  // ─── Scan → result / noscan ───────────────────────────────────────────────

  async function startScan() {
    if (!overlays || !pending || !state.pin) return;
    // The trim counts only if it was made for this pin (it's reset on every new pin)
    const trim = state.adjust !== "off" ? state.trim : null;
    const geo = state.geo;
    let corners: [LL, LL, LL, LL];
    if (trim && geo) corners = trimCorners(geo, trim);
    else if (state.outline) corners = outlineCorners(state.outline);
    else {
      const p = state.pin;
      const n = offset(p, 12, 0);
      const s = offset(p, -12, 0);
      corners = [offset(n, -12, 90), offset(n, 12, 90), offset(s, 12, 90), offset(s, -12, 90)];
    }
    overlays.clearAll();
    overlays.pin.hide();
    state.acceptMapClicks = false;
    setStep("scan");
    state.checklist = 0;
    setChip(CHIP[0]);
    render();

    // Checklist timing: item 1 after the fly-in, item 2 at 40%, item 3 at 75%
    const tick = (i: number) => {
      if (state.step !== "scan" || state.checklist >= i) return;
      state.checklist = i;
      setChip(CHIP[i]);
      render();
    };
    window.setTimeout(() => tick(1), 500);
    // The roof (already asked for on the confirm step), then the figures for the panels
    // kept on this house. Both run while the sweep plays, so the scan never waits twice.
    const result = pending.promise.then(
      async (b) => {
        const kept = trim && geo ? inSlice(b.panels, trim, geo) : b.panels;
        const est = await requestEstimate(b, kept);
        return { ok: true as const, b, kept, est };
      },
      (e: Error & { code?: string }) => ({ ok: false as const, e }),
    ).catch((e: Error & { code?: string }) => ({ ok: false as const, e }));
    await Promise.all([overlays.runScan(corners, (f) => (f >= 0.75 ? tick(3) : f >= 0.4 ? tick(2) : undefined)), result]);
    if (destroyed || state.step !== "scan") return;
    overlays.clearScanOverlays();
    state.checklist = 4;
    setChip(null);
    const r = await result;
    if (!r.ok) {
      return showNoscan(r.e?.message || "Our roof scan is unavailable right now, an installer can still check it for you.");
    }
    state.kept = r.kept;
    enterResult(r.est);
  }

  /** Ask the API for the figures for these panels at the current usage. */
  function requestEstimate(b: SolarRoof, kept: SolarRoofPanel[]): Promise<SolarEstimate> {
    // Best-first, and no system goes above 60 panels: send the best 200 only (a commercial
    // roof can hold hundreds, which the API used to reject as "invalid panel yields")
    return api.solarEstimate!({ kwhDc: kept.slice(0, 200).map((p) => p.kwhDc), refWatts: b.refWatts, usageKwh: state.usage });
  }

  function showNoscan(reason: string) {
    view3dOff();
    overlays?.clearAll();
    if (state.pin) overlays?.pin.place(state.pin, false);
    state.noscanReason = reason.charAt(0).toUpperCase() + reason.slice(1).replace(/([^.])$/, "$1.");
    state.offered = [];
    setStep("noscan");
    render();
  }

  /** The result (§7.5): panels best-first, slider, figures and price, all from the API. */
  function enterResult(est: SolarEstimate) {
    if (est.tooSmall || !est.rows.length) {
      state.estimate = null;
      return showNoscan("Your roof looks too small for a standard system. An installer can still take a look.");
    }
    state.estimate = est;
    state.minP = est.min;
    state.maxP = est.max;
    state.offered = state.kept.slice(0, est.max);
    state.recommended = est.recommended;
    state.n = est.recommended;
    state.battery = state.battery && est.battery.offered;
    state.fromNoscan = false;
    setStep("result");
    overlays?.buildPanels(state.offered);
    render();
    if (state.building) centreOn(state.building.center);
    void overlays?.revealPanels(state.n);
  }

  /**
   * New figures for the same panels (usage changed, or the console's draft prices did):
   * keep the result on screen, move the slider to the new recommendation.
   */
  async function reestimate() {
    const b = state.building;
    if (state.step !== "result" || !b || !state.kept.length) return;
    const ticket = ++estimating;
    state.busy = true;
    render();
    try {
      const est = await requestEstimate(b, state.kept);
      if (ticket !== estimating || destroyed) return;
      state.busy = false;
      state.error = null;
      if (est.tooSmall || !est.rows.length) return enterResult(est);
      const sameMax = est.max === state.maxP;
      state.estimate = est;
      state.minP = est.min;
      state.maxP = est.max;
      state.recommended = est.recommended;
      state.n = est.recommended;
      state.battery = state.battery && est.battery.offered;
      if (!sameMax) {
        // A different biggest system: rebuild the panel polygons for the new offer
        state.offered = state.kept.slice(0, est.max);
        overlays?.buildPanels(state.offered);
        resetView3d();
      }
      overlays?.showPanels(state.n);
      view3d?.show(state.n);
    } catch (e) {
      if (ticket !== estimating) return;
      state.busy = false;
      state.error = (e as Error).message;
    }
    render();
  }

  // ─── 3D view (§7.6) ───────────────────────────────────────────────────────

  async function toggle3d() {
    if (!state.building?.view3d || !state.estimate) return;
    if (state.show3d) {
      view3dOff();
      render();
      return;
    }
    try {
      if (!view3d) view3d = await createView3d(map3dDiv, state.building, state.offered, state.n);
      else view3d.show(state.n);
      map3dDiv.hidden = false;
      mapWrap.hidden = true;
      state.show3d = true;
      state.error = null;
    } catch {
      state.error = "3D view isn't available for this property.";
    }
    render();
  }

  function view3dOff() {
    state.show3d = false;
    map3dDiv.hidden = true;
    mapWrap.hidden = false;
  }

  function resetView3d() {
    view3d?.destroy();
    view3d = null;
    view3dOff();
  }

  // ─── Restart (§7.7): clear everything, back to the address step ───────────

  function restart() {
    resetView3d();
    overlays?.clearAll();
    overlays?.pin.hide();
    setChip(null);
    pending = null;
    Object.assign(state, {
      error: null,
      busy: false,
      location: null,
      pin: null,
      approx: false,
      justMoved: false,
      acceptMapClicks: false,
      buildingReady: false,
      building: null,
      buildingError: null,
      outline: null,
      fitted: false,
      geo: null,
      adjust: "off",
      trim: null,
      kept: [],
      estimate: null,
      offered: [],
      fromNoscan: false,
    });
    if (map) {
      map.setZoom(6);
      map.setCenter(UK_VIEW);
    }
    flownIn = false;
    setStep("address");
    render();
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  function markOpened() {
    if (opened) return;
    opened = true;
    api.track?.("opened");
  }

  async function searchPostcode(pc: string) {
    state.postcode = pc.trim().toUpperCase();
    if (!POSTCODE_RE.test(state.postcode)) {
      state.error = "Enter a valid UK postcode.";
      return render();
    }
    markOpened();
    state.busy = true;
    state.error = null;
    render();
    try {
      state.addresses = await api.searchAddresses(state.postcode);
      if (!state.addresses.length) state.error = "We couldn't find any addresses for that postcode. Use \"My address isn't listed\" instead.";
    } catch (e) {
      state.error = (e as Error).message;
    }
    state.busy = false;
    render();
  }

  /** Geocode the chosen address, then go to the confirm step. */
  async function locate(req: SolarLocateRequest) {
    markOpened();
    state.busy = true;
    state.error = null;
    render();
    try {
      const loc = await api.solarLocate!(req);
      state.location = loc;
      state.approx = loc.approx;
      state.chosen = req;
      state.busy = false;
      await enterConfirm({ lat: loc.lat, lng: loc.lng });
    } catch (e) {
      state.busy = false;
      state.error = (e as Error).message;
      render();
    }
  }

  async function sendLead() {
    if (!state.name.trim()) state.error = "Please enter your name.";
    else if (!EMAIL_RE.test(state.email.trim())) state.error = "Please enter a valid email address.";
    else if (!state.consent) state.error = "Please tick the box so we can contact you.";
    else state.error = null;
    if (state.error) return render();
    const r = rowFor(state.n);
    const lead: SolarLead = {
      product: "solar",
      address: state.location?.address ?? "",
      // The address as chosen or typed (house, postcode), for the installer's Leads page
      line: state.chosen?.line ?? ([state.chosen?.house, state.chosen?.postcode].filter(Boolean).join(", ") || undefined),
      uprn: state.chosen?.uprn,
      lat: state.pin?.lat ?? 0,
      lng: state.pin?.lng ?? 0,
      name: state.name.trim(),
      email: state.email.trim(),
      phone: state.phone.trim() || undefined,
      consent: true,
      system:
        !state.fromNoscan && r
          ? {
              panels: state.n,
              battery: withBattery(),
              usageKwh: state.usage,
              shown: {
                annualKwh: Math.round(r.kwh),
                annualSavingGbp: Math.round(withBattery() ? r.savingBattery : r.saving),
                roofMaxPanels: state.kept.length,
                imageryQuality: state.building?.imageryQuality ?? "",
              },
            }
          : undefined,
    };
    state.busy = true;
    render();
    try {
      await api.submitSolarLead!(lead);
      state.busy = false;
      setStep("done");
    } catch (e) {
      state.busy = false;
      state.error = (e as Error).message;
    }
    render();
  }

  // Clicks (delegated on the card)
  card.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const addr = t.closest<HTMLElement>("[data-addr]");
    if (addr) {
      const a = state.addresses.find((x) => x.id === addr.dataset.addr);
      if (a) void locate({ line: a.line, uprn: a.id, postcode: state.postcode });
      return;
    }
    const action = t.closest<HTMLElement>("[data-action]")?.dataset.action;
    switch (action) {
      case "manual":
        state.manual = true;
        state.error = null;
        return render();
      case "list":
        state.manual = false;
        state.error = null;
        return render();
      case "adjust":
        if (!state.geo) return;
        state.adjust = "manual";
        state.trim = wholeRow();
        drawConfirmOverlays();
        return render();
      case "move":
        // Leave adjust mode first: there the outline is locked (only the slice handles
        // move) and a tap only slides the slice along the row, so nothing could move.
        // With adjust off the outline is draggable and a tap on another roof moves the pin.
        state.adjust = "off";
        state.trim = null;
        state.acceptMapClicks = true;
        drawConfirmOverlays();
        setChip("Drag the outline onto your house, or tap your roof");
        return render();
      case "yes":
        if (state.buildingReady) void startScan();
        return;
      case "3d":
        return void toggle3d();
      case "quote":
        state.fromNoscan = false;
        setStep("contact");
        return render();
      case "ask":
        state.fromNoscan = true;
        setStep("contact");
        return render();
      case "notmine":
        return reconfirm();
      case "restart":
        return restart();
      case "back":
        if (state.fromNoscan) setStep("noscan");
        else if (state.estimate) {
          setStep("result");
          render();
          return;
        }
        return render();
    }
  });

  // Forms (delegated)
  card.addEventListener("submit", (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    switch (form.dataset.form) {
      case "postcode":
        return void searchPostcode(String(data.get("postcode") ?? ""));
      case "manual": {
        state.house = String(data.get("house") ?? "").trim();
        state.street = String(data.get("street") ?? "").trim();
        state.town = String(data.get("town") ?? "").trim();
        state.postcode = String(data.get("postcode") ?? "").trim().toUpperCase();
        if (!state.house) state.error = "Enter your house name or number.";
        else if (!state.town) state.error = "Enter your town or village.";
        else if (!POSTCODE_RE.test(state.postcode)) state.error = "Enter a valid UK postcode.";
        else state.error = null;
        if (state.error) return render();
        // The full address, as an address list entry would give it: it finds the house more
        // reliably, and it's what the installer sees on Leads ("The Barn, West Top, Slaithwaite, HD7 5UU")
        return void locate({ line: [state.house, state.street, state.town, state.postcode].filter(Boolean).join(", ").slice(0, 200), house: state.house, postcode: state.postcode });
      }
      case "usage": {
        const v = Number(data.get("usage"));
        if (!Number.isFinite(v) || v < USAGE.MIN || v > USAGE.MAX) {
          state.error = `Enter a yearly usage between ${USAGE.MIN.toLocaleString("en-GB")} and ${USAGE.MAX.toLocaleString("en-GB")} kWh.`;
          return render();
        }
        // New usage: the API works out new figures and recommendation, the slider moves to it
        state.usage = Math.round(v / USAGE.STEP) * USAGE.STEP;
        state.error = null;
        return void reestimate();
      }
      case "contact":
        state.name = String(data.get("name") ?? "");
        state.email = String(data.get("email") ?? "");
        state.phone = String(data.get("phone") ?? "");
        state.consent = data.get("consent") !== null;
        return void sendLead();
    }
  });

  // Slider and battery: update in place
  card.addEventListener("input", (e) => {
    const t = e.target as HTMLInputElement;
    if (t.dataset.action === "slider") {
      state.n = Number(t.value);
      overlays?.showPanels(state.n);
      view3d?.show(state.n);
      updateResultFigures();
    }
  });
  card.addEventListener("change", (e) => {
    const t = e.target as HTMLInputElement;
    if (t.dataset.action === "battery") {
      state.battery = t.checked;
      updateResultFigures();
    }
  });

  // ─── Console preview: jump to a step with sample data ─────────────────────

  async function goTo(step: SolarStep | string) {
    const target = step as SolarStep;
    if (target === "address") return restart();
    // Every later step needs an address and a confirmed building
    if (!state.location || !state.building) {
      const sample = { line: "The Barn, West Top, Slaithwaite, HD7 5UU", house: "The Barn", postcode: "HD7 5UU" };
      const loc = await api.solarLocate!(sample);
      state.location = loc;
      state.approx = false;
      state.chosen = sample;
      await enterConfirm({ lat: loc.lat, lng: loc.lng });
      await pending?.promise.catch(() => undefined);
    }
    if (target === "confirm") return reconfirm();
    if (target === "scan") return void startScan();
    const b = state.building;
    if (target === "noscan") return showNoscan("We don't have aerial roof data for this exact spot yet, an installer can still check it for you.");
    if (!b) return;
    overlays?.clearAll();
    overlays?.pin.hide();
    if (state.step !== "result" || !state.estimate) {
      // The whole roof (no slice), priced by the API like a real scan
      state.kept = b.panels;
      try {
        enterResult(await requestEstimate(b, state.kept));
      } catch (e) {
        return showNoscan((e as Error).message);
      }
    }
    if (target === "contact" || target === "done") {
      state.fromNoscan = false;
      setStep(target);
      render();
    }
  }

  // ─── First paint ──────────────────────────────────────────────────────────

  layout();
  render();
  opts.onStep?.("address");
  // Keep the MIN_SCAN_MS import meaningful for readers: the scan step can't finish sooner
  void MIN_SCAN_MS;

  return {
    update(next) {
      branding = next;
      layout();
      if (state.step === "confirm") drawConfirmOverlays();
      render();
    },
    goTo,
    refresh() {
      void reestimate();
    },
    destroy() {
      destroyed = true;
      pending = null;
      resetView3d();
      overlays?.clearAll();
      shadow.innerHTML = "";
    },
  };
}
